import { getProperty, getPropertyAnytimeRate, getPropertyRates } from 'api/accommodation'
import buildPropertyRatesKey from 'lib/offer/buildPropertyRatesKey'
import { getSource } from 'selectors/offerSelectors'

import { API_CALL, PROPERTY_UPDATE_PRICING_DATA } from './actionConstants'
import {
  FETCH_PROPERTY,
  FETCH_PROPERTY_ANYTIME_RATE,
  FETCH_PROPERTY_RATES,
} from './apiActionConstants'
import type { AppAction } from './types/ActionTypes'

export function fetchProperty(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (!propertyId || state.offer.properties[propertyId]) return

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY,
      request: () => getProperty(propertyId, {
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
        previewMode: state.system.previewMode,
      }),
      propertyId,
    })
  }
}

export function fetchPropertyAnytimeRate(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (!propertyId || state.offer.propertyAnytimeRates[propertyId]) return

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_ANYTIME_RATE,
      request: () => getPropertyAnytimeRate(propertyId, {
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
      }),
      propertyId,
    })
  }
}

interface PropertyRatesParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  refresh?: boolean
}

export function fetchPropertyRates(propertyId: string, {
  checkIn,
  checkOut,
  occupancies,
  utmContent,
  roomTypeId,
  roomRateId,
  refresh = false,
}: PropertyRatesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const source = getSource(state)

    const key = buildPropertyRatesKey({
      propertyId,
      checkIn,
      checkOut,
      occupancies,
      utmContent,
      roomTypeId,
      roomRateId,
    })
    // NB: For better or worse, we have no TTL on cached responses, so rates _will_ become stale and
    // errors _won't_ clear until a hard refresh.
    if (!key || (!refresh && state.offer.propertyRates[key])) return

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_RATES,
      request: () => getPropertyRates(propertyId, {
        checkIn,
        checkOut,
        occupancies,
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
        previewMode: state.system.previewMode,
        source,
        dynamicAvailability: true,
        utmContent,
        roomTypeId,
        roomRateId,
      }),
      key,
    })
  }
}

interface PropertyPricingDataParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  roomId: string
  rateId: string
  newPrice: number
  newLuxPlusPrice: number | undefined
  newPropertyFees: number
}

export function updatePropertyPricingData(propertyId: string, {
  checkIn,
  checkOut,
  occupancies,
  utmContent,
  roomTypeId,
  roomRateId,
  roomId,
  rateId,
  newPrice,
  newLuxPlusPrice,
  newPropertyFees,
}: PropertyPricingDataParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const key = buildPropertyRatesKey({
      propertyId,
      checkIn,
      checkOut,
      occupancies,
      utmContent,
      roomTypeId,
      roomRateId,
    })
    if (!key || !state.offer.propertyRates[key]) return

    dispatch({
      type: PROPERTY_UPDATE_PRICING_DATA,
      key,
      roomId,
      rateId,
      newPrice,
      newLuxPlusPrice,
      newPropertyFees,
    })
  }
}
